import React, { useEffect, useRef } from 'react';
import { Spin, Table, Button } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { type AppDispatch } from '../../../redux/store';
import fmt from 'indian-number-format';
import {
  getUnapprovedSalesAction,
  updateUnapprovedSaleAction,
} from '../../Sales/store/salesActions';

interface Sale {
  _id: string;
  saleNumber: string;
  customer: any;
  principal: number;
  documentCharge: number;
  approvalType: string;
  calculatedPrincipal: number;
  calculatedDocumentCharges: number;
  saleAverage: number;
  jewels: any[];
  [key: string]: any;
}

export default function SalesApprovalsIndex(): JSX.Element {
  const dispatch = useDispatch<AppDispatch>();

  const { unapprovedSales }: any = useSelector<any>((state) => state?.sales);

  const getSales = async (): Promise<void> => {
    await getUnapprovedSalesAction(dispatch, {});
    processingRef.current = false;
  };

  useEffect(() => {
    void getSales();
  }, []);

  const processingRef = useRef(false);

  const handleUpdate = async (sale: Sale, status: string) => {
    if (processingRef.current) return;
    processingRef.current = true;
    await updateUnapprovedSaleAction(dispatch, { id: sale._id, status });
    void getSales();
    processingRef.current = false;
  };

  const columns = [
    {
      title: 'S.No',
      dataIndex: 'sno',
      render: (_: any, record: Sale, index: number) => index + 1,
      width: 50,
    },
    {
      title: 'Sale Number',
      dataIndex: 'saleNumber',
      key: 'saleNumber',
      render: (_: any, record: Sale) => <p>{record.saleNumber}</p>,
    },
    {
      title: 'Customer',
      dataIndex: 'customer',
      key: 'customer',
      render: (_: any, record: Sale) => (
        <img src={record.customer?.photo} style={{ height: 130, width: 130 }} />
      ),
    },
    {
      title: 'Principal',
      dataIndex: 'principal',
      key: 'principal',
      width: 60,
      render: (_: any, record: Sale) => (<p>{record.principal != null ? `₹${fmt.format(record.principal)}` : '-'}</p>),
    },
    {
      title: 'Approval Type',
      dataIndex: 'approvalType',
      key: 'approvalType',
      width: 80,
      render: (_: any, record: Sale) => <p>{record.approvalType}</p>,
    },
    {
      title: 'Sale Average',
      dataIndex: 'saleAverage',
      key: 'saleAverage',
      width: 60,
      render: (_: any, record: Sale) => (<p>{record.saleAverage != null ? `₹${fmt.format(Math.round(record.saleAverage))}` : '-'}</p>),
    },
    {
      title: 'Total Gross Weight',
      dataIndex: 'grossWeight',
      key: 'grossWeight',
      width: 60,
      render: (_: any, record: Sale) => (
        <p>
          {record.jewels
            ?.reduce(
              (total: number, jewel: any) => total + jewel.grossWeight,
              0
            )
            ?.toFixed(2) ?? 0}
        </p>
      ),
    },
    {
      title: 'Total Net Weight',
      dataIndex: 'netWeight',
      key: 'netWeight',
      width: 60,
      render: (_: any, record: Sale) => (
        <p>
          {record.jewels
            ?.reduce((total: number, jewel: any) => total + jewel.netWeight, 0)
            ?.toFixed(2) ?? 0}
        </p>
      ),
    },
    {
      title: 'Calculated Principal',
      dataIndex: 'calculatedPrincipal',
      key: 'calculatedPrincipal',
      width: 60,
      render: (_: any, record: Sale) => (
        <p>{record.calculatedPrincipal != null ? `₹${fmt.format(record.calculatedPrincipal)}` : '-'}</p>
      ),
    },
    {
      title: 'Requested Principal',
      dataIndex: 'requestedPrincipal',
      key: 'requestedPrincipal',
      width: 60,
      render: (_: any, record: Sale) => (<p>{record.principal != null ? `₹${fmt.format(record.principal)}` : '-'}</p>),
    },
    {
      title: 'Calculated Document Charge',
      dataIndex: 'calculatedDocumentCharge',
      key: 'calculatedDocumentCharge',
      width: 60,
      render: (_: any, record: Sale) => (
        <p>{record.calculatedDocumentCharges != null ? `₹${fmt.format(record.calculatedDocumentCharges)}` : '-'}</p>
      ),
    },
    {
      title: 'Requested Document Charge',
      dataIndex: 'requestedDocumentCharge',
      key: 'requestedDocumentCharge',
      width: 60,
      render: (_: any, record: Sale) => (<p>{record.documentCharge != null ? `₹${fmt.format(record.documentCharge)}` : '-'}</p>),
    },
    {
      title: 'Approve',
      dataIndex: 'actions',
      width: 100,
      render: (_: any, record: Sale) => (
        <Button
          type="primary"
          style={{
            backgroundColor: '#387F39',
            borderColor: '#387F39',
          }}
          onClick={() => {
            if (!processingRef.current) {
              void handleUpdate(record, 'approve');
            }
          }}
        >
          Approve
        </Button>
      ),
    },
    {
      title: 'Reject',
      dataIndex: 'actions2',
      width: 100,
      render: (_: any, record: Sale) => (
        <Button
          type="primary"
          style={{
            backgroundColor: '#DD1E2D',
            borderColor: '#DD1E2D',
          }}
          onClick={() => {
            if (!processingRef.current) {
              void handleUpdate(record, 'reject');
            }
          }}
        >
          Reject
        </Button>
      ),
    },
  ];

  return (
    <div className="users__table__page">
      {!(unapprovedSales.length > 0) && <Spin spinning={false} fullscreen />}
      <div style={{ width: '100%' }}>
        <Table
          dataSource={unapprovedSales}
          columns={columns}
          rowKey="_id"
          pagination={false}
        />
      </div>
    </div>
  );
}
